/**
 * A text link.
 *
 * The XAML shape this replaces is a `Button` with `Background="Transparent"`, `BorderThickness="0"`,
 * `Padding="0"` and a brand foreground — written out at eleven sites, because there was nothing else
 * to say "this is a link, not a button". A real one matters here for a reason beyond tidiness: a
 * bordered button stretched across the content column reads as a primary action, and these are
 * navigational asides ("View archived customers →", "N devices are waiting for approval →") that
 * must not compete with the New / Save / Approve buttons beside them.
 *
 * The colour comes from the theme at paint time, like every other role in this app - `linkColor` is
 * assigned in `onPaint` rather than at construction so a theme switch moves it.
 */
import { LinkLabel, type LinkLabelOptions, type RenderContext, type Size, type Theme } from 'webwin';
import { fitSize } from './Text.js';
import { crm } from '../theme/CrmTheme.js';
import { FONT } from './layout.js';

export type LinkTone = 'brand' | 'warn' | 'danger';

export interface CrmLinkOptions extends LinkLabelOptions {
  tone?: LinkTone;
}

export class CrmLink extends LinkLabel {
  tone: LinkTone;

  constructor(options: CrmLinkOptions = {}) {
    super({
      font: FONT.small,
      autoWidth: true,
      autoHeight: true,
      hAlign: 'start',
      // Wraps rather than running off a phone's edge; one line, as wide as its text, when it fits.
      wrap: true,
      ...options
    });
    this.tone = options.tone ?? 'brand';
  }

  override preferredSize(available: Size): Size {
    return fitSize(this, available) ?? super.preferredSize(available);
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const c = crm(theme);
    this.linkColor = this.tone === 'warn' ? c.warn : this.tone === 'danger' ? c.danger : c.brand;
    super.onPaint(rc, theme);
  }
}

export function link(
  text: string,
  onClick: () => void,
  tone: LinkTone = 'brand',
  options: CrmLinkOptions = {}
): CrmLink {
  return new CrmLink({ text, tone, onClick, ...options });
}
